import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { getErrorMessage, getErrorType, ERROR_TYPES } from '../constants/errorMessages';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      // Guest checkout relies on an httpOnly guestCartId cookie (see
      // backend/src/middleware/cartIdentity.ts) to keep a guest's cart, hold,
      // and checkout session tied to one browser across requests.
      withCredentials: true,
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        this.handleRequestError(error);
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        this.handleResponseError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleRequestError(error: unknown): void {
    const axiosError = error as AxiosError;
    console.error('API Request Error:', axiosError);
    
    // Log to Sentry
    Sentry.captureException(axiosError, {
      contexts: {
        api: {
          type: 'request',
          url: axiosError.config?.url,
          method: axiosError.config?.method,
        },
      },
    });
  }

  private handleResponseError(error: AxiosError): void {
    const errorType = getErrorType(error);
    const userMessage = getErrorMessage(error);
    
    console.error('API Response Error:', {
      status: error.response?.status,
      message: userMessage,
      url: error.config?.url,
      method: error.config?.method,
    });

    // Handle authentication errors
    if (error.response?.status === 401) {
      // Token expired or invalid
      console.warn('401 Unauthorized - clearing token and signing out');
      localStorage.removeItem('accessToken');
      
      // Force sign out from OIDC to break the loop
      // Don't just redirect to '/' which could cause a loop
      const oidcStorage = sessionStorage.getItem(`oidc.user:${process.env.REACT_APP_COGNITO_AUTHORITY}:${process.env.REACT_APP_COGNITO_CLIENT_ID}`);
      if (oidcStorage) {
        sessionStorage.removeItem(`oidc.user:${process.env.REACT_APP_COGNITO_AUTHORITY}:${process.env.REACT_APP_COGNITO_CLIENT_ID}`);
      }
      
      // Clear all OIDC storage
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('oidc.')) {
          sessionStorage.removeItem(key);
        }
      });
      
      window.location.href = '/';
      return;
    }

    // Log to Sentry based on error type
    const shouldLogToSentry = 
      errorType === ERROR_TYPES.SERVER || 
      errorType === ERROR_TYPES.UNKNOWN ||
      (errorType === ERROR_TYPES.NETWORK && process.env.NODE_ENV === 'production');

    if (shouldLogToSentry) {
      Sentry.captureException(error, {
        contexts: {
          api: {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
            requestId: (error.response?.data as { requestId?: string })?.requestId,
          },
          response: {
            data: error.response?.data,
          },
        },
        tags: {
          errorType,
          httpStatus: error.response?.status,
        },
        level: errorType === ERROR_TYPES.SERVER ? 'error' : 'warning',
      });
    }

    // Add user-friendly message to error object
    (error as { userMessage?: string }).userMessage = userMessage;
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const api = new ApiClient();

